import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";

// Core layout components - always loaded
import Header from "./components/Header";
import Footer from "./components/Footer";
import FinalCtaBand from "./components/FinalCtaBand";
import LocalHeader from "./components/LocalHeader";
import LocalFooter from "./components/LocalFooter";
// Lazy load non-critical UI components to improve FCP
const ExitIntentPopup = lazy(() => import("./components/ExitIntentPopup"));
const StickyBookingButton = lazy(() => import("./components/StickyBookingButton"));
const ServicesChat = lazy(() => import("./components/ServicesChat"));
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";

// Lazy load all page components for code splitting
const FunnelLanding = lazy(() => import("./pages/FunnelLanding"));
const PaidAdsFunnel = lazy(() => import("./pages/PaidAdsFunnel"));

const Home = lazy(() => import("./pages/Home"));
const HomeV4 = lazy(() => import("./pages/HomeV4"));
const HomeV3 = lazy(() => import("./pages/HomeV3"));
const HomeV2 = lazy(() => import("./pages/HomeV2"));
const HomeOriginal = lazy(() => import("./pages/archive/HomeOriginal"));
const HomeLegacy = lazy(() => import("./pages/archive/HomeLegacy"));
const HomeV5 = lazy(() => import("./pages/archive/HomeV5"));
const ThankYouWebsite = lazy(() => import("./pages/ThankYouWebsite"));
const ThankYouLinkedInSDR = lazy(() => import("./pages/ThankYouLinkedInSDR"));
const ThankYouPaidAds = lazy(() => import("./pages/ThankYouPaidAds"));
const ThankYouBreakdown = lazy(() => import("./pages/ThankYouBreakdown"));
const DemandGeneration = lazy(() => import("./pages/DemandGeneration"));
const AccountBasedMarketing = lazy(() => import("./pages/AccountBasedMarketing"));
const PipelineAcceleration = lazy(() => import("./pages/PipelineAcceleration"));
const About = lazy(() => import("./pages/About"));
const AboutMe = lazy(() => import("./pages/AboutMe"));
const Culture = lazy(() => import("./pages/Culture"));
const OurCommitments = lazy(() => import("./pages/OurCommitments"));
const Pricing = lazy(() => import("./pages/Pricing"));
const WebsiteDesign = lazy(() => import("./pages/WebsiteDesign"));
const WebsitePortfolio = lazy(() => import("./pages/WebsitePortfolio"));
const Resources = lazy(() => import("./pages/Resources"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const WhitePapers = lazy(() => import("./pages/WhitePapers"));
const ContentPolicy = lazy(() => import("./pages/ContentPolicy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const SEOLocalSearch = lazy(() => import("./pages/SEOLocalSearch"));
const AccessionEngine = lazy(() => import("./pages/AccessionEngine"));
const SEOCaseStudy = lazy(() => import("./pages/SEOCaseStudy"));
const VAEnergyCaseStudy = lazy(() => import("./pages/VAEnergyCaseStudy"));
const CaseStudiesPage = lazy(() => import("./pages/CaseStudies"));
const BookCall = lazy(() => import("./pages/BookCall"));
const BookingIntermediary = lazy(() => import("./pages/BookingIntermediary"));
const PaidAds = lazy(() => import("./pages/PaidAds"));
const GrowthStart = lazy(() => import("./pages/GrowthStart"));
const BusinessAccelerator = lazy(() => import("./pages/BusinessAccelerator"));
const EnterpriseDomination = lazy(() => import("./pages/EnterpriseDomination"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));

const GrowthGateway = lazy(() => import("./pages/GrowthGateway"));
const GatewayAnalytics = lazy(() => import("./pages/GatewayAnalytics"));
const LeadsAnalytics = lazy(() => import("./pages/LeadsAnalytics"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const LinkedInSDR = lazy(() => import("./pages/LinkedInSDR"));
const ThankYouBooking = lazy(() => import("./pages/ThankYouBooking"));
const ConsultationConfirm = lazy(() => import("./pages/ConsultationConfirm"));
const LocalHome = lazy(() => import("./pages/local/LocalHome"));
const LocalGetStarted = lazy(() => import("./pages/local/LocalGetStarted"));
const LocalServices = lazy(() => import("./pages/local/LocalServices"));
const LocalPricing = lazy(() => import("./pages/local/LocalPricing"));
const LocalTrades = lazy(() => import("./pages/local/LocalTrades"));
const LocalRestaurants = lazy(() => import("./pages/local/LocalRestaurants"));
const LocalSalons = lazy(() => import("./pages/local/LocalSalons"));
const LocalMedical = lazy(() => import("./pages/local/LocalMedical"));
const AIKnowledgeBase = lazy(() => import("./pages/AIKnowledgeBase"));
const Glossary = lazy(() => import("./pages/Glossary"));
const WebsiteLeadsDashboard = lazy(() => import("./pages/WebsiteLeadsDashboard"));
const UnifiedLeadsDashboard = lazy(() => import("./pages/UnifiedLeadsDashboard"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SecondaryOnboardingPage = lazy(() => import("./pages/SecondaryOnboarding"));
const WebsiteOnboardingEnquiry = lazy(() => import("./pages/WebsiteOnboardingEnquiry"));
const WebsiteOnboardingFull = lazy(() => import("./pages/WebsiteOnboardingFull"));
const WebsiteOnboardingUnsubscribe = lazy(() => import("./pages/WebsiteOnboardingUnsubscribe"));
const HealthChecker = lazy(() => import("./pages/HealthChecker"));
const HeatmapDashboard = lazy(() => import("./pages/HeatmapDashboard"));
const ClientMetrics = lazy(() => import("./pages/ClientMetrics"));
const ClientPortalPage = lazy(() => import("./pages/ClientPortal"));
const ClientDetailPage = lazy(() => import("./pages/ClientDetail"));
const UserSettingsPage = lazy(() => import("./pages/UserSettings"));
const RevenueDashboard = lazy(() => import("./pages/RevenueDashboard"));
const OGShareImage = lazy(() => import("./pages/OGShareImage"));

// Hidden VSL funnel pages — not linked from public navigation
const VSLSelector = lazy(() => import("./pages/vsl/VSLSelector"));
const VSLWebsites = lazy(() => import("./pages/vsl/VSLWebsites"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SolutionAnalytics = lazy(() => import("./pages/admin/SolutionAnalytics"));
const ClaudeChat = lazy(() => import("./pages/admin/ClaudeChat"));
const ClientPrompts = lazy(() => import("./pages/admin/ClientPrompts"));
const BackendHealth = lazy(() => import("./pages/admin/BackendHealth"));
const ClientReviews = lazy(() => import("./pages/admin/ClientReviews"));
const ExportAuditLog = lazy(() => import("./pages/admin/ExportAuditLog"));
const WebsiteOnboardingSubmissions = lazy(() => import("./pages/admin/WebsiteOnboardingSubmissions"));
const NewOnboardingSubmissions = lazy(() => import("./pages/admin/NewOnboardingSubmissions"));
const PendingStage2Enquiries = lazy(() => import("./pages/admin/PendingStage2Enquiries"));
const NotificationHistory = lazy(() => import("./pages/admin/NotificationHistory"));
const AdminPriorityQueue = lazy(() => import("./pages/admin/AdminPriorityQueue"));
const LeadMagnetSubmissions = lazy(() => import("./pages/admin/LeadMagnetSubmissions"));
const LeadMagnetFunnelAnalytics = lazy(() => import("./pages/admin/LeadMagnetFunnelAnalytics"));
const KeywordMap = lazy(() => import("./pages/admin/KeywordMap"));
const VSLLeadGen = lazy(() => import("./pages/vsl/VSLLeadGen"));
const VSLLinkedIn = lazy(() => import("./pages/vsl/VSLLinkedIn"));

// SEO pillar + cluster pages (B2B Lead Generation topic cluster)
const B2BLeadGeneration = lazy(() => import("./pages/seo/B2BLeadGeneration"));
const B2BLeadGenerationLondon = lazy(() => import("./pages/seo/B2BLeadGenerationLondon"));
const B2BLeadGenerationManchester = lazy(() => import("./pages/seo/B2BLeadGenerationManchester"));
const B2BLeadGenerationLeeds = lazy(() => import("./pages/seo/B2BLeadGenerationLeeds"));
const B2BLeadGenerationBirmingham = lazy(() => import("./pages/seo/B2BLeadGenerationBirmingham"));
const B2BLeadGenerationBristol = lazy(() => import("./pages/seo/B2BLeadGenerationBristol"));
const B2BLeadGenerationEdinburgh = lazy(() => import("./pages/seo/B2BLeadGenerationEdinburgh"));
const B2BLeadGenerationUKCities = lazy(() => import("./pages/seo/B2BLeadGenerationUKCities"));
const LinkedInLeadGeneration = lazy(() => import("./pages/seo/LinkedInLeadGeneration"));
const B2BLeadGenerationCost = lazy(() => import("./pages/seo/B2BLeadGenerationCost"));
const B2BLeadGenerationVsInHouse = lazy(() => import("./pages/seo/B2BLeadGenerationVsInHouse"));
const VSLConsulting = lazy(() => import("./pages/vsl/VSLConsulting"));

// Client review pages — sent privately to existing clients
const TwoMonthReview = lazy(() => import("./pages/reviews/TwoMonthReview"));
const SixMonthReview = lazy(() => import("./pages/reviews/SixMonthReview"));
const OneYearReview = lazy(() => import("./pages/reviews/OneYearReview"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-primary text-lg">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

// Global handler to redirect recovery links to /auth
const RecoveryRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if URL hash contains recovery token and we're not already on /auth
    if (window.location.hash.includes("type=recovery") && location.pathname !== "/auth") {
      // Preserve the hash when redirecting to /auth
      navigate(`/auth${window.location.hash}`, { replace: true });
    }
  }, [navigate, location.pathname]);

  return null;
};

// Fire a GA4 pageview on every SPA route change so Search Console + GA4
// see accurate page paths (gtag config has send_page_view:false in index.html).
const RouteAnalytics = () => {
  const location = useLocation();
  useEffect(() => {
    // Defer to next tick so the new <title> from each page has applied.
    const id = window.setTimeout(() => {
      import("@/lib/analytics").then(({ trackPageview }) => {
        trackPageview(location.pathname + location.search);
      });
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search]);
  return null;
};

// Layout wrapper for main site pages
const MainLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main id="main-content" className="flex-1 pt-16" tabIndex={-1}>
      {children}
    </main>
    <FinalCtaBand />
    <Footer />
    <Suspense fallback={null}>
      <ExitIntentPopup />
      <StickyBookingButton />
    </Suspense>
    
  </div>
);

// Layout wrapper for local business subdomain pages - separate navigation
const LocalLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen flex flex-col">
    <LocalHeader />
    <main id="main-content" className="flex-1 pt-16" tabIndex={-1}>
      {children}
    </main>
    <LocalFooter />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Toaster />
    <Sonner />
    <BrowserRouter>
      <ScrollToTop />
      <RecoveryRedirectHandler />
      <RouteAnalytics />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Standalone funnel pages - no header/footer */}
          {/* Gateway page - main traffic destination */}
          <Route path="/start" element={<GrowthGateway source="organic" />} />
          <Route path="/start/fb" element={<GrowthGateway source="facebook" />} />
          <Route path="/start/ig" element={<GrowthGateway source="instagram" />} />
          <Route path="/start/li" element={<GrowthGateway source="linkedin" />} />
          <Route path="/start/gads" element={<GrowthGateway source="google-ads" />} />
          <Route path="/start/email" element={<GrowthGateway source="email" />} />
          <Route path="/start/ref" element={<GrowthGateway source="referral" />} />
          
          
          {/* Thank You pages */}
          <Route path="/thank-you-booking" element={<ThankYouBooking />} />
          <Route path="/confirm" element={<ConsultationConfirm />} />
          <Route path="/book" element={<BookingIntermediary />} />
          <Route path="/thank-you-website" element={<ThankYouWebsite />} />
          <Route path="/thank-you-linkedin-sdr" element={<ThankYouLinkedInSDR />} />
          <Route path="/thank-you-paid-ads" element={<ThankYouPaidAds />} />
          <Route path="/thank-you-breakdown" element={<ThankYouBreakdown />} />
          
          {/* Secondary onboarding - email link only */}
          <Route path="/onboarding/website" element={<SecondaryOnboardingPage />} />
          
          <Route path="/app" element={<LeadsAnalytics />} />
          <Route path="/og-preview" element={<OGShareImage />} />

          {/* Main site with header/footer */}
          <Route path="/" element={<MainLayout><Home /></MainLayout>} />
          <Route path="/apply" element={<FunnelLanding />} />
          <Route path="/go" element={<PaidAdsFunnel />} />

          {/* Client Portal - admin list */}
          <Route 
            path="/admin/clients" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <ClientPortalPage />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          {/* Client Detail - admin OR the specific client */}
          <Route 
            path="/admin/clients/:clientId" 
            element={
              <MainLayout>
                <ProtectedRoute requireAdmin={false}>
                  <ClientDetailPage />
                </ProtectedRoute>
              </MainLayout>
            } 
          />

          <Route path="/v4" element={<HomeV4 />} />
          <Route path="/v3" element={<MainLayout><HomeV3 /></MainLayout>} />
          <Route path="/home-v2" element={<MainLayout><HomeV2 /></MainLayout>} />
          
          {/* Homepage Archives - for LinkedIn case study on website evolution */}
          <Route path="/archive/home-original" element={<MainLayout><HomeOriginal /></MainLayout>} />
          <Route path="/archive/home-legacy" element={<MainLayout><HomeLegacy /></MainLayout>} />
          <Route path="/archive/home-v2" element={<MainLayout><HomeV2 /></MainLayout>} />
          <Route path="/home-legacy" element={<MainLayout><HomeLegacy /></MainLayout>} />
          <Route path="/archive/home-v5" element={<MainLayout><HomeV5 /></MainLayout>} />
          <Route path="/about" element={<MainLayout><About /></MainLayout>} />
          <Route path="/about-me" element={<MainLayout><AboutMe /></MainLayout>} />
          <Route path="/culture" element={<MainLayout><Culture /></MainLayout>} />
          <Route path="/our-commitments" element={<MainLayout><OurCommitments /></MainLayout>} />
          <Route path="/pricing" element={<MainLayout><Pricing /></MainLayout>} />
          <Route path="/website-design" element={<MainLayout><WebsiteDesign /></MainLayout>} />
          <Route path="/accession-engine" element={<MainLayout><AccessionEngine /></MainLayout>} />
          <Route path="/website-portfolio" element={<MainLayout><WebsitePortfolio /></MainLayout>} />
          <Route path="/resources" element={<MainLayout><Resources /></MainLayout>} />
          <Route path="/blog" element={<MainLayout><BlogPage /></MainLayout>} />
          <Route path="/blog/:slug" element={<MainLayout><BlogPost /></MainLayout>} />
          <Route path="/blog-old" element={<MainLayout><Blog /></MainLayout>} />
          <Route path="/white-papers" element={<MainLayout><WhitePapers /></MainLayout>} />
          <Route path="/enterprise" element={<MainLayout><Enterprise /></MainLayout>} />
          <Route path="/demand-generation" element={<MainLayout><DemandGeneration /></MainLayout>} />
          <Route path="/abm" element={<MainLayout><AccountBasedMarketing /></MainLayout>} />
          <Route path="/pipeline-acceleration" element={<MainLayout><PipelineAcceleration /></MainLayout>} />
          <Route path="/paid-ads" element={<MainLayout><PaidAds /></MainLayout>} />
          <Route path="/linkedin-sdr" element={<MainLayout><LinkedInSDR /></MainLayout>} />

          {/* B2B Lead Generation SEO topic cluster */}
          <Route path="/b2b-lead-generation" element={<MainLayout><B2BLeadGeneration /></MainLayout>} />
          <Route path="/b2b-lead-generation/london" element={<MainLayout><B2BLeadGenerationLondon /></MainLayout>} />
          <Route path="/b2b-lead-generation/manchester" element={<MainLayout><B2BLeadGenerationManchester /></MainLayout>} />
          <Route path="/b2b-lead-generation/leeds" element={<MainLayout><B2BLeadGenerationLeeds /></MainLayout>} />
          <Route path="/b2b-lead-generation/birmingham" element={<MainLayout><B2BLeadGenerationBirmingham /></MainLayout>} />
          <Route path="/b2b-lead-generation/bristol" element={<MainLayout><B2BLeadGenerationBristol /></MainLayout>} />
          <Route path="/b2b-lead-generation/edinburgh" element={<MainLayout><B2BLeadGenerationEdinburgh /></MainLayout>} />
          <Route path="/b2b-lead-generation/uk-cities" element={<MainLayout><B2BLeadGenerationUKCities /></MainLayout>} />
          <Route path="/linkedin-lead-generation" element={<MainLayout><LinkedInLeadGeneration /></MainLayout>} />
          <Route path="/b2b-lead-generation-cost" element={<MainLayout><B2BLeadGenerationCost /></MainLayout>} />
          <Route path="/b2b-lead-generation-vs-in-house" element={<MainLayout><B2BLeadGenerationVsInHouse /></MainLayout>} />

          <Route path="/seo-local-search" element={<MainLayout><SEOLocalSearch /></MainLayout>} />
          <Route path="/seo-case-study" element={<MainLayout><SEOCaseStudy /></MainLayout>} />
          <Route path="/case-studies" element={<MainLayout><CaseStudiesPage /></MainLayout>} />
          <Route path="/case-study/va-energy" element={<MainLayout><VAEnergyCaseStudy /></MainLayout>} />
          <Route path="/book-call" element={<MainLayout><BookCall /></MainLayout>} />
          <Route path="/content-policy" element={<MainLayout><ContentPolicy /></MainLayout>} />
          <Route path="/faq" element={<MainLayout><FAQ /></MainLayout>} />
          <Route path="/growth-start" element={<MainLayout><GrowthStart /></MainLayout>} />
          <Route path="/business-accelerator" element={<MainLayout><BusinessAccelerator /></MainLayout>} />
          <Route path="/enterprise-domination" element={<MainLayout><EnterpriseDomination /></MainLayout>} />
          <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
          <Route path="/terms-of-service" element={<MainLayout><TermsOfService /></MainLayout>} />
          <Route path="/auth" element={<MainLayout><Auth /></MainLayout>} />
          <Route path="/website-onboarding" element={<WebsiteOnboardingEnquiry />} />
          <Route path="/website-onboarding/full" element={<WebsiteOnboardingFull />} />
          <Route path="/website-onboarding/unsubscribe" element={<WebsiteOnboardingUnsubscribe />} />
          <Route 
            path="/admin" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          <Route 
            path="/admin/keyword-map" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <KeywordMap />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          <Route 
            path="/admin/gateway-analytics" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <GatewayAnalytics />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          <Route 
            path="/admin/website-leads" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <WebsiteLeadsDashboard />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          <Route 
            path="/admin/leads" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <UnifiedLeadsDashboard />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          
          {/* Customer Dashboard - any authenticated user */}
          <Route 
            path="/dashboard" 
            element={
              <MainLayout>
                <ProtectedRoute requireAdmin={false}>
                  <Dashboard />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          
          {/* User Settings */}
          <Route 
            path="/settings" 
            element={
              <MainLayout>
                <ProtectedRoute requireAdmin={false}>
                  <UserSettingsPage />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          
          {/* Client Metrics Dashboard */}
          <Route 
            path="/admin/metrics" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <ClientMetrics />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          
          {/* Revenue Dashboard */}
          <Route 
            path="/admin/revenue" 
            element={
              <MainLayout>
                <ProtectedRoute requireAdmin>
                  <RevenueDashboard />
                </ProtectedRoute>
              </MainLayout>
            } 
          />
          
          {/* AI Knowledge Base - Optimized for AI agents */}
          <Route path="/ai-knowledge-base" element={<MainLayout><AIKnowledgeBase /></MainLayout>} />
          
          {/* Glossary - Complete site directory */}
          <Route path="/glossary" element={<MainLayout><Glossary /></MainLayout>} />
          
          {/* SEO Health Checker - Internal Tool (not in navigation) */}
          <Route path="/healthchecker" element={<MainLayout><HealthChecker /></MainLayout>} />
          <Route 
            path="/admin/heatmap" 
            element={
              <MainLayout>
                <ProtectedRoute>
                  <HeatmapDashboard />
                </ProtectedRoute>
              </MainLayout>
            } 
          />

          {/* Solution funnel pages — no header/footer, no public links */}
          <Route path="/solution" element={<VSLSelector />} />
          <Route path="/solution/websites" element={<VSLWebsites />} />
          <Route path="/solution/leadgen" element={<VSLLeadGen />} />
          <Route path="/solution/linkedin" element={<VSLLinkedIn />} />
          <Route path="/solution/consulting" element={<VSLConsulting />} />

          {/* Legacy redirects from /vsl/x* → /solution */}
          <Route path="/vsl/x9" element={<Navigate to="/solution" replace />} />
          <Route path="/vsl/x9/websites" element={<Navigate to="/solution/websites" replace />} />
          <Route path="/vsl/x9/leadgen" element={<Navigate to="/solution/leadgen" replace />} />
          <Route path="/vsl/x9/linkedin" element={<Navigate to="/solution/linkedin" replace />} />
          <Route path="/vsl/x9/consulting" element={<Navigate to="/solution/consulting" replace />} />
          <Route path="/vsl/*" element={<Navigate to="/solution" replace />} />

          {/* Tier-0 fix: nav-linked routes that were 404ing */}
          <Route path="/solutions" element={<Navigate to="/solution" replace />} />
          <Route path="/solutions/*" element={<Navigate to="/solution" replace />} />
          <Route path="/websites" element={<Navigate to="/solution/websites" replace />} />

          {/* Client review forms (private, unlisted) */}
          <Route path="/two-month-review" element={<TwoMonthReview />} />
          <Route path="/six-month-review" element={<SixMonthReview />} />
          <Route path="/one-year-review" element={<OneYearReview />} />
          <Route path="/year-review" element={<OneYearReview />} />

          {/* Solution funnel: unsubscribe + admin analytics */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/admin/solution-analytics" element={<MainLayout><SolutionAnalytics /></MainLayout>} />
          <Route path="/admin/claude" element={<MainLayout><ClaudeChat /></MainLayout>} />
          <Route path="/admin/prompts" element={<MainLayout><ClientPrompts /></MainLayout>} />
          <Route path="/admin/backend-health" element={<MainLayout><ProtectedRoute><BackendHealth /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/client-reviews" element={<MainLayout><ProtectedRoute><ClientReviews /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/export-audit-log" element={<MainLayout><ProtectedRoute><ExportAuditLog /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/website-onboarding" element={<MainLayout><ProtectedRoute><WebsiteOnboardingSubmissions /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/website-onboarding/new" element={<MainLayout><ProtectedRoute><NewOnboardingSubmissions /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/website-onboarding/pending" element={<MainLayout><ProtectedRoute><PendingStage2Enquiries /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/notification-history" element={<MainLayout><ProtectedRoute><NotificationHistory /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/priority-queue" element={<MainLayout><ProtectedRoute><AdminPriorityQueue /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/lead-magnet" element={<MainLayout><ProtectedRoute><LeadMagnetSubmissions /></ProtectedRoute></MainLayout>} />
          <Route path="/admin/lead-magnet/funnel" element={<MainLayout><ProtectedRoute><LeadMagnetFunnelAnalytics /></ProtectedRoute></MainLayout>} />

          {/* Local Business subdomain - separate layout with LocalHeader/LocalFooter */}
          <Route path="/local/a" element={<LocalLayout><LocalHome /></LocalLayout>} />
          <Route path="/local/a/get-started" element={<LocalLayout><LocalGetStarted /></LocalLayout>} />
          <Route path="/local/a/services" element={<LocalLayout><LocalServices /></LocalLayout>} />
          <Route path="/local/a/pricing" element={<LocalLayout><LocalPricing /></LocalLayout>} />
          <Route path="/local/a/trades" element={<LocalLayout><LocalTrades /></LocalLayout>} />
          <Route path="/local/a/restaurants" element={<LocalLayout><LocalRestaurants /></LocalLayout>} />
          <Route path="/local/a/salons" element={<LocalLayout><LocalSalons /></LocalLayout>} />
          <Route path="/local/a/medical" element={<LocalLayout><LocalMedical /></LocalLayout>} />
          
          <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
        </Routes>
      </Suspense>
      <Suspense fallback={null}>
        <ServicesChat />
      </Suspense>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
